import { create } from 'zustand';

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  targetRole?: 'admin' | 'user'; // Which session type this notification belongs to
  link?: string | null;
}

interface NotificationStore {
  notifications: Notification[];
  unreadCount: number;
  currentSessionType: 'admin' | 'user' | null;
  setSessionType: (type: 'admin' | 'user' | null) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  upsertNotifications: (notifications: Notification[]) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  getFilteredNotifications: () => Notification[];
  getFilteredUnreadCount: () => number;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  currentSessionType: null,
  
  setSessionType: (type) => set({ currentSessionType: type }),
  
  addNotification: (notification) => set((state) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
      // Default to 'user' if not specified (for backward compatibility)
      targetRole: notification.targetRole || 'user',
    };
    return {
      notifications: [newNotification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    };
  }),

  upsertNotifications: (incoming) => set((state) => {
    const byId = new Map<string, Notification>();
    for (const n of state.notifications) byId.set(n.id, n);
    for (const n of incoming) {
      const prev = byId.get(n.id);
      byId.set(n.id, prev ? { ...prev, ...n } : n);
    }
    const merged = Array.from(byId.values()).sort((a, b) => {
      const at = new Date(a.timestamp).getTime();
      const bt = new Date(b.timestamp).getTime();
      return bt - at;
    });
    const unread = merged.filter((n) => !n.read).length;
    return { notifications: merged, unreadCount: unread };
  }),
  
  markAsRead: (id) => set((state) => {
    const notification = state.notifications.find((n) => n.id === id);
    const wasUnread = notification && !notification.read;
    return {
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
      unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    };
  }),
  
  markAllAsRead: () => set((state) => {
    const filtered = state.currentSessionType 
      ? state.notifications.filter(n => n.targetRole === state.currentSessionType)
      : state.notifications;
    const unreadInFiltered = filtered.filter(n => !n.read).length;
    return {
      notifications: state.notifications.map((n) => {
        // Only mark as read if it matches current session type
        if (state.currentSessionType && n.targetRole !== state.currentSessionType) {
          return n;
        }
        return { ...n, read: true };
      }),
      unreadCount: Math.max(0, state.unreadCount - unreadInFiltered),
    };
  }),
  
  removeNotification: (id) => set((state) => {
    const notification = state.notifications.find((n) => n.id === id);
    return {
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount: notification && !notification.read ? state.unreadCount - 1 : state.unreadCount,
    };
  }),
  
  clearAll: () => set({ notifications: [], unreadCount: 0 }),
  
  getFilteredNotifications: () => {
    const state = get();
    if (!state.currentSessionType) {
      return state.notifications;
    }
    return state.notifications.filter(n => n.targetRole === state.currentSessionType);
  },
  
  getFilteredUnreadCount: () => {
    const state = get();
    if (!state.currentSessionType) {
      return state.unreadCount;
    }
    return state.notifications.filter(
      n => n.targetRole === state.currentSessionType && !n.read
    ).length;
  },
}));